import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCamera, FaCheck } from 'react-icons/fa'
import { getSupportDomain } from '@renderer/utils/brandUtils'

interface NewspaperConsentModalProps {
  onConsent: (consent: boolean) => void
  isVisible: boolean
  stripPreview?: string
  mode?: 'capture' | 'record'
  timeout?: number // Optional timeout in seconds, defaults to 30
}

const NewspaperConsentModal: React.FC<NewspaperConsentModalProps> = ({
  onConsent,
  isVisible,
  stripPreview,
  mode,
  timeout = 30
}) => {
  const { t } = useTranslation()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect((): (() => void) => {
    if (isVisible && timeout > 0) {
      // Start invisible timeout
      timeoutRef.current = setTimeout(() => {
        // Auto-decline consent after timeout
        onConsent(false)
      }, timeout * 1000)
    }

    // Cleanup timeout on unmount or when visibility changes
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isVisible, timeout, onConsent])

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2">
      <div className="bg-newspaper-white border-4 border-double border-[#2a2a2a] w-full h-full max-w-7xl max-h-[95vh] mx-2 overflow-hidden">
        {/* Content */}
        <div className="flex h-full">
          {/* Left side - Full height strip image */}
          <div className="flex-1 flex justify-center items-center p-8 bg-newspaper-white">
            {stripPreview ? (
              <div className="h-full flex items-center justify-center">
                {mode === 'record' ? (
                  <video src={stripPreview} controls className="h-full w-auto object-contain" />
                ) : (
                  <img
                    src={stripPreview}
                    alt={t('consent.photoStripAlt', 'Photo Strip')}
                    className="h-full w-auto object-contain"
                    crossOrigin="anonymous"
                  />
                )}
              </div>
            ) : (
              <div className="h-full w-80 flex items-center justify-center">
                <FaCamera className="text-6xl text-gray-400" />
              </div>
            )}
          </div>

          {/* Right side - Content */}
          <div className="flex-1 flex flex-col justify-center p-12 bg-newspaper-white">
            {/* Title and Question */}
            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-newspaper-black mb-6 font-newspaper uppercase tracking-wider">
                {t('consent.newspaper.headline', 'READY TO BE FAMOUS?')}
              </h1>
              <p className="text-2xl text-newspaper-black/80 leading-relaxed font-newspaper">
                {t('consent.question', 'Can we use your photo strip for publication?')}
              </p>
            </div>

            {/* Points */}
            <div className="mb-12">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-newspaper-black p-3 mt-1 rounded-none">
                    <FaCheck className="text-newspaper-white text-lg" />
                  </div>
                  <span className="text-newspaper-black text-xl font-newspaper">
                    {t('consent.benefits.featured', 'Featured on our platforms')}
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-newspaper-black p-3 mt-1 rounded-none">
                    <FaCheck className="text-newspaper-white text-lg" />
                  </div>
                  <span className="text-newspaper-black text-xl font-newspaper">
                    {t('consent.benefits.inspire', 'Inspire others')}
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-newspaper-black p-3 mt-1 rounded-none">
                    <FaCheck className="text-newspaper-white text-lg" />
                  </div>
                  <span className="text-newspaper-black text-xl font-newspaper">
                    {t('consent.benefits.privacy', 'Privacy protected')}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={() => onConsent(true)}
                className="border-2 border-double border-[#2a2a2a] font-newspaper font-bold transition-all duration-200 hover:opacity-90 w-full text-2xl bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none px-12 py-5 tracking-wider"
              >
                {t('consent.accept', "Yes, I'd love to!")}
              </button>
              <button
                type="button"
                onClick={() => onConsent(false)}
                className="border-2 border-double border-[#2a2a2a] font-newspaper font-bold transition-all duration-200 hover:opacity-90 w-full text-2xl bg-[#fafaf8] text-[#1a1a1a] rounded-none shadow-none px-12 py-5 tracking-wider"
              >
                {t('consent.decline', 'No, thanks')}
              </button>
            </div>

            {/* Footer Note */}
            <div className="mt-6 text-center">
              <p className="text-base text-newspaper-black/60 font-newspaper">
                {t(
                  'consent.footer',
                  `You can change this preference anytime by contacting our support team at ${getSupportDomain()}.`
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewspaperConsentModal
